import React, {ReactNode} from 'react'
import {FormattedMessage} from 'react-intl'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'

import {ButtonLink} from '@vio/atlas/ButtonLink'
import {Box} from '@vio/atlas/helpers/Box'
import {Grid} from '@vio/atlas/helpers/Grid'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {Tooltip, TooltipPlacement} from '@vio/atlas/Tooltip'
import {defaultCurrencyCode} from '@vio/core/src/localization/business/currency'
import {CurrencyDisplayVariant} from '@vio/core/src/price/business/formatPrice'

import {Price} from '../../search/Price'
import {NUMBER_OF_DECIMALS_FOR_PRICE} from '.'
import {PriceDetailsSkeleton} from './Skeleton'

interface PriceLineProps {
  label?: ReactNode
  price: number
  currencyCode?: string
  showFeesDescriptions?: boolean
  reducedMargin?: boolean
  tooltipBody?: ReactNode
  icon?: ReactNode
  isPriceLoading?: boolean
  onInfoClick?: () => void
  currencyDisplayVariant?: CurrencyDisplayVariant
  isPayAtProperty?: boolean
}

const PriceLineWrapper = styled.div`
  &.--with-reduced-margin {
    margin-bottom: ${cssTheme.layout.spacing.s300};
  }
`

export const StyledText = styled(Text)`
  text-decoration: underline;
  text-decoration-style: dotted;
  text-decoration-color: ${cssTheme.colors.content.neutral.secondary};
  display: flex;
  align-items: center;
  text-underline-offset: 4px;
  cursor: pointer;
`

const StyledPrice = styled(Price)`
  ${cssTheme.typography.bodyM}
`

const TextRow = styled.div`
  column-gap: ${cssTheme.layout.spacing.s200};
  display: flex;
  flex-wrap: wrap;
  align-items: center;
`

export const PriceLine = ({
  label,
  price,
  showFeesDescriptions,
  tooltipBody,
  icon,
  onInfoClick,
  currencyCode = defaultCurrencyCode,
  isPriceLoading = false,
  reducedMargin = false,
  currencyDisplayVariant,
  isPayAtProperty = false
}: PriceLineProps) => {
  return (
    <PriceLineWrapper className={cx(reducedMargin && '--with-reduced-margin')}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        flexWrap="nowrap"
        gap="s500"
      >
        <TextRow>
          {showFeesDescriptions ? (
            <Box display="flex" alignItems="center" flexWrap="nowrap">
              {tooltipBody ? (
                <Tooltip
                  placement={TooltipPlacement.TopStart}
                  content={tooltipBody}
                >
                  <StyledText variant="bodyM">{label}</StyledText>
                </Tooltip>
              ) : (
                <ButtonLink
                  onClick={onInfoClick}
                  size="md"
                  isCompact
                  variant="secondary"
                  isDotted
                  data-id="taxes-and-fees-button"
                >
                  <Text variant="bodyM" colorPath="content.neutral.primary">
                    {label}
                  </Text>
                </ButtonLink>
              )}
              {icon}
            </Box>
          ) : (
            <Text variant="bodyM">{label}</Text>
          )}
          {isPayAtProperty && (
            <div>
              <Text variant="bodyS" colorPath="content.neutral.secondary">
                <FormattedMessage
                  id="priceDetails.payAtProperty"
                  defaultMessage="(pay at property)"
                />
              </Text>
            </div>
          )}
        </TextRow>

        <Grid mobileSm="fit">
          <Grid container direction="column" alignItems="flex-end">
            <Grid>
              {isPriceLoading ? (
                <PriceDetailsSkeleton />
              ) : (
                <StyledPrice
                  noStyle
                  maximumFractionDigits={NUMBER_OF_DECIMALS_FOR_PRICE}
                  minimumFractionDigits={NUMBER_OF_DECIMALS_FOR_PRICE}
                  value={price}
                  currency={currencyCode}
                  currencyDisplayVariant={currencyDisplayVariant}
                />
              )}
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </PriceLineWrapper>
  )
}
